import { ideaRoomFloorPlanUrl, ideaRoomNotes } from "./ideaRoomExport.js";

/** Plant packet slots the manufacturing org asks dealers to send. */

export type PlantPacketSlotSpec = {
  item_key: string;
  label: string;
  required: boolean;
};

export type PlantPacketLayer = "ops" | "plant";

/** v1 default when the manufacturer has not authored plant slots yet. */
export const DEFAULT_PLANT_PACKET_SLOTS: readonly PlantPacketSlotSpec[] = [
  { item_key: "order_form", label: "Order form", required: true },
  { item_key: "floor_plan", label: "Floor plan", required: true },
  { item_key: "special_instructions", label: "Special instructions", required: false },
];

const LEGACY_PLANT_PACKET_KEYS: Record<string, string> = {
  order_details: "floor_plan",
  custom_options: "special_instructions",
};

const PLANT_KEYS = new Set([
  ...DEFAULT_PLANT_PACKET_SLOTS.map((s) => s.item_key),
  ...Object.keys(LEGACY_PLANT_PACKET_KEYS),
]);

export function isPlantPacketItemKey(itemKey: string): boolean {
  return PLANT_KEYS.has(itemKey);
}

/** Prefer stored packet_layer; fall back to the v1 hardcoded keys. */
export function isPlantPacketItem(row: {
  item_key: string;
  packet_layer?: string | null;
}): boolean {
  if (row.packet_layer === "plant") return true;
  if (row.packet_layer === "ops") return false;
  return isPlantPacketItemKey(row.item_key);
}

/** Canonical key if this row is (or was) a plant-packet slot. */
export function canonicalPlantPacketKey(itemKey: string): string | null {
  if (DEFAULT_PLANT_PACKET_SLOTS.some((s) => s.item_key === itemKey)) return itemKey;
  return LEGACY_PLANT_PACKET_KEYS[itemKey] ?? null;
}

/** Optional slots seed waived; required slots seed missing. */
export function plantPacketSlotSeedStatus(
  itemKey: string,
  required?: boolean
): "missing" | "waived" {
  if (typeof required === "boolean") return required ? "missing" : "waived";
  return itemKey === "special_instructions" || itemKey === "custom_options" ? "waived" : "missing";
}

export function defaultPlantSlotRequired(itemKey: string): boolean {
  const spec = DEFAULT_PLANT_PACKET_SLOTS.find((s) => s.item_key === itemKey);
  if (spec) return spec.required;
  const canon = canonicalPlantPacketKey(itemKey);
  if (canon && canon !== itemKey) return defaultPlantSlotRequired(canon);
  return true;
}

export function plantPacketCoveredKeys(existingKeys: ReadonlySet<string>): Set<string> {
  const covered = new Set<string>();
  for (const key of existingKeys) {
    const canon = canonicalPlantPacketKey(key);
    if (canon) covered.add(canon);
  }
  return covered;
}

export type PlantPacketEvidenceHint = {
  item_key: string;
  evidence_url: string;
};

function httpUrl(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const s = raw.trim();
  if (!/^https?:\/\//i.test(s)) return null;
  return s;
}

export function parsePlantPacketHttpUrl(
  raw: string,
  label: string
): { ok: true; value: string | null } | { ok: false; error: string } {
  const s = raw.trim();
  if (!s) return { ok: true, value: null };
  const url = httpUrl(s);
  if (!url) return { ok: false, error: `${label} must be an http(s) URL.` };
  return { ok: true, value: url };
}

/**
 * Host captured: `invoice_url` (order PDF), `building_floor_plan_url` (inventory floor-plan PDF).
 * In-app form: `floor_plan_url`. IdeaRoom: `order.images.floorplanWithLegendSrc` (render PNG).
 * Do not use `3d_design_url` (configurator homepage, not a file).
 */
export function plantPacketEvidenceFromPayload(
  body: Record<string, unknown>
): PlantPacketEvidenceHint[] {
  const out: PlantPacketEvidenceHint[] = [];
  const invoice = httpUrl(body.invoice_url);
  if (invoice) out.push({ item_key: "order_form", evidence_url: invoice });
  const floor =
    httpUrl(body.floor_plan_url) ?? httpUrl(body.building_floor_plan_url) ?? ideaRoomFloorPlanUrl(body);
  if (floor) out.push({ item_key: "floor_plan", evidence_url: floor });
  return out;
}

/** @deprecated use plantPacketEvidenceFromPayload */
export function plantPacketEvidenceFromHostPayload(
  body: Record<string, unknown>
): PlantPacketEvidenceHint[] {
  return plantPacketEvidenceFromPayload(body);
}

/** Stamp in-app URL fields onto intake raw_payload. */
export function applyPlantPacketUrlsToPayload(
  raw: Record<string, unknown>,
  urls: { orderFormUrl: string; floorPlanUrl: string }
): { ok: true } | { ok: false; error: string } {
  const order = parsePlantPacketHttpUrl(urls.orderFormUrl, "Order form URL");
  if (!order.ok) return order;
  const floor = parsePlantPacketHttpUrl(urls.floorPlanUrl, "Floor plan URL");
  if (!floor.ok) return floor;
  if (order.value) raw.invoice_url = order.value;
  if (floor.value) raw.floor_plan_url = floor.value;
  return { ok: true };
}

/**
 * In-app intake stamps `notes` on raw_payload. When present, special instructions
 * are no longer optional on the plant packet.
 */
export function plantPacketSpecialInstructionsFromPayload(
  body: Record<string, unknown>
): string | null {
  const notes = typeof body.notes === "string" ? body.notes.trim() : "";
  return notes || ideaRoomNotes(body);
}
